import { useEffect, memo, useMemo, useState } from "react";

import { FontManager } from "@samuelmeuli/font-manager";
import { noop, toLabelAndValue } from "neetocist";
import { globalProps } from "neetocommons/initializers";
import Select from "neetoui/Select";
import PropTypes from "prop-types";
import { mergeLeft } from "ramda";

import { DEFAULT_PICKER_PROPS } from "./constants";
import { sortAndFormatFonts } from "./utils";

const GoogleFontPicker = ({
  activeFontFamily,
  fontManagerOptions = { limit: 300 },
  onChange = noop,
  ...otherProps
}) => {
  const [selectedFontFamily, setSelectedFontFamily] =
    useState(activeFontFamily);
  const [isLoading, setIsLoading] = useState(false);
  const [fonts, setFonts] = useState([]);

  const options = mergeLeft(fontManagerOptions, DEFAULT_PICKER_PROPS);

  useEffect(() => {
    initializeFontManager();
  }, []);

  const fontManager = useMemo(
    () =>
      new FontManager(
        globalProps.googleFontsApiKey,
        selectedFontFamily,
        options,
        onChange
      ),
    []
  );

  const initializeFontManager = async () => {
    try {
      setIsLoading(true);
      await fontManager.init();
      const availableFonts = Array.from(fontManager.getFonts().values());
      setFonts(sortAndFormatFonts(availableFonts));
    } catch (error) {
      throw error.message;
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = fontFamily => {
    const { value } = fontFamily;
    fontManager.setActiveFont(value, false);
    setSelectedFontFamily(value);
    onChange(value);
  };

  return (
    <Select
      data-testid="google-font-picker"
      options={fonts}
      strategy="fixed"
      value={toLabelAndValue(selectedFontFamily)}
      styles={{
        option: (styles, { data }) => ({
          ...styles,
          fontFamily: data.value,
        }),
        singleValue: (styles, { data }) => ({
          ...styles,
          fontFamily: data.value,
        }),
      }}
      {...{ ...otherProps, isLoading }}
      onChange={handleChange}
    />
  );
};

GoogleFontPicker.propTypes = {
  /**
   * Specifies the currently selected font family
   */
  activeFontFamily: PropTypes.string,
  /**
   * Function receives the newly selected font family name as its argument.
   * Use it to update the parent component's state or perform any other necessary actions when the font changes.
   */
  onChange: PropTypes.func,
  /**
   * Object that configures the FontManager's options.
   * These options are merged with the default configuration extracted from @samuelmeuli/font-manager.
   */
  fontManagerOptions: PropTypes.shape({
    limit: PropTypes.number,
    pickerId: PropTypes.string,
    families: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.arrayOf(PropTypes.string),
    scripts: PropTypes.arrayOf(PropTypes.string),
    variants: PropTypes.arrayOf(PropTypes.string),
    filter: PropTypes.func,
    sort: PropTypes.oneOf(["alphabet", "popularity"]),
  }),
  /**
   * Any extra props not explicitly defined will be spread onto the Select component. This can include properties
   * like placeholder, isDisabled, or any other prop accepted by the `neetoui` Select component.
   */
  otherProps: PropTypes.shape({}),
};

export default memo(GoogleFontPicker);
